import React, { useState } from "react";
import "./WeddingDetails.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCocktail,
  faFireFlameCurved,
  faSnowflake,
  faLeaf,
  faUtensils,
  faCookieBite,
} from "@fortawesome/free-solid-svg-icons";

// Define passcode metadata
const passcodeInfo: Record<string, { title: string }> = {
  "Abby Hillner": { title: "Abby Hillner's Schedule" },
  "Aiden Prud'Homme": { title: "Aiden Prud'Homme's Schedule" },
  "Alana Luebke": { title: "Alana Luebke's Schedule" },
  "Alex Moran": { title: "Alex Moran's Schedule" },
  "Amber Gebka": { title: "Amber Gebka's Schedule" },
  "Amber Micoliczyk": { title: "Amber Micoliczyk's Schedule" },
  "Ann Young": { title: "Ann Young's Schedule" },
  "Ashlynn Micoliczyk": { title: "Ashlynn Micoliczyk's Schedule" },
  "Autumn Beyer": { title: "Autumn Beyer's Schedule" },
  "Bart Gebka": { title: "Bart Gebka's Schedule" },
  "Brie Standlee": { title: "Brie Standlee's Schedule" },
  "Caroline Bank": { title: "Caroline Bank's Schedule" },
  "Danielle Conner": { title: "Danielle Conner's Schedule" },
  "Faith Standlee": { title: "Faith Standlee's Schedule" },
  "Gabby Harwood": { title: "Gabby Harwood's Schedule" },
  "Heather Quint": { title: "Heather Quint's Schedule" },
  "James Conner": { title: "James Conner's Schedule" },
  "Jim Conner": { title: "Jim Conner's Schedule" },
  "Joe Young": { title: "Joe Young's Schedule" },
  "Jonny Keane": { title: "Jonny Keane's Schedule" },
  "Jordan Green": { title: "Jordan Green's Schedule" },
  "Justin Benning": { title: "Justin Benning's Schedule" },
  "Karen Micoliczyk": { title: "Karen Micoliczyk's Schedule" },
  "Ken Browne": { title: "Ken Browne's Schedule" },
  "Kevin Quint": { title: "Kevin Quint's Schedule" },
  "Kevin Standlee": { title: "Kevin Standlee's Schedule" },
  "Kolton Micoliczyk": { title: "Kolton Micoliczyk's Schedule" },
  "Kolya Isterabadi": { title: "Kolya Isterabadi's Schedule" },
  "Luke Harwood": { title: "Luke Harwood's Schedule" },
  "Mark Micoliczyk": { title: "Mark Micoliczyk's Schedule" },
  "Mary Jane Cramer": { title: "Mary Jane Cramer's Schedule" },
  "Michael Conner": { title: "Michael Conner's Schedule" },
  "Michelle Standlee": { title: "Michelle Standlee's Schedule" },
  "Mike Adams": { title: "Mike Adams's Schedule" },
  "Mitchell Johnstone": { title: "Mitchell Johnstone's Schedule" },
  "Nikhil Gajghate": { title: "Nikhil Gajghate's Schedule" },
  "Riley Wright": { title: "Riley Wright's Schedule" },
  "Ryan Kruk": { title: "Ryan Kruk's Schedule" },
  "Sam Keyser": { title: "Sam Keyser's Schedule" },
  "Sarah Conner": { title: "Sarah Conner's Schedule" },
  "Sue Browne": { title: "Sue Browne's Schedule" },
  "Yeji Shin": { title: "Yeji Shin's Schedule" },
};

// Define itinerary item type
interface ItineraryItem {
  time: string;
  title: string;
  description?: string;
  venue?: string;
  venueLink?: string;
  addressLine1?: string;
  addressLine2?: string;
  note?: string;
  icon?: string;
  requiredPasscodes?: string[]; // Item only visible if one of these passcodes is active (empty/undefined = always visible)
  isSpecial?: boolean; // Whether to render as a special card
}

// All itinerary items in chronological order
const allItineraryItems: ItineraryItem[] = [
  {
    time: "5:00 PM Thanksgiving Day",
    icon: "/icons/turkey.png",
    title: "Thanksgiving Dinner",
    venue: "Michael and Ashlynn's House",
    addressLine1: "573 Westfield Way Unit E",
    addressLine2: "Pewaukee, WI 53072",
    requiredPasscodes: [
      "Amber Micoliczyk",
      "Ann Young",
      "Ashlynn Micoliczyk",
      "Danielle Conner",
      "James Conner",
      "Jim Conner",
      "Joe Young",
      "Karen Micoliczyk",
      "Ken Browne",
      "Kolton Micoliczyk",
      "Mark Micoliczyk",
      "Michael Conner",
      "Mike Adams",
      "Sarah Conner",
      "Sue Browne",
    ],
  },
  {
    time: "11:30 AM - 2:00 PM the day beforehand",
    icon: "/icons/sandwich.png",
    title: "Rehearsal Lunch",
    venue: "Ally's Bistro",
    addressLine1: "N72W13350 Lund Ln",
    addressLine2: "Menomonee Falls, WI 53051",
    requiredPasscodes: [
      "Alana Luebke",
      "Alex Moran",
      "Amber Micoliczyk",
      "Amber Gebka",
      "Bart Gebka",
      "Brie Standlee",
      "Danielle Conner",
      "Faith Standlee",
      "James Conner",
      "Jim Conner",
      "Karen Micoliczyk",
      "Kolton Micoliczyk",
      "Mark Micoliczyk",
      "Michelle Standlee",
      "Riley Wright",
      "Sarah Conner",
      "Yeji Shin",
    ],
    isSpecial: true,
  },
  {
    time: "6:00 PM the day beforehand",
    title: "Games and Revelry",
    icon: "/icons/board-game.png",
    venue: "Michael and Ashlynn's House",
    addressLine1: "573 Westfield Way Unit E",
    addressLine2: "Pewaukee, WI 53072",
    requiredPasscodes: [
      "Abby Hillner",
      "Alana Luebke",
      "Alex Moran",
      "Amber Micoliczyk",
      "Ashlynn Micoliczyk",
      "Autumn Beyer",
      "Bart Gebka",
      "Caroline Bank",
      "Gabby Harwood",
      "James Conner",
      "Jonny Keane",
      "Jordan Green",
      "Justin Benning",
      "Kolton Micoliczyk",
      "Kolya Isterabadi",
      "Luke Harwood",
      "Mary Jane Cramer",
      "Michael Conner",
      "Mitchell Johnstone",
      "Nikhil Gajghate",
      "Riley Wright",
      "Ryan Kruk",
      "Sam Keyser",
      "Sarah Conner",
      "Yeji Shin",
    ],
    isSpecial: true,
  },
  {
    time: "9:00 AM - 1:00 PM",
    title: "Bridesmaids Prep",
    description:
      "Hair, makeup, and getting ready photos. Meet at the bridal suite! Hair must be dry, clean, and product free. Bring your dresses and accessories to the Airbnb the day beforehand.",
    icon: "/icons/brush.png",
    venue: "Plankinton Arcade Airbnb",
    addressLine1: "161 West Wisconsin Avenue",
    addressLine2: "Milwaukee, WI 53203",
    requiredPasscodes: [
      "Alana Luebke",
      "Amber Micoliczyk",
      "Ashlynn Micoliczyk",
      "Riley Wright",
      "Yeji Shin",
    ],
    isSpecial: true,
  },
  {
    time: "10:30 AM - 1:00 PM",
    title: "Groomsmen Prep",
    description:
      "NO LATER THAN 10:45 AM. Bring your suits and accessories to Bart's place the day beforehand. We are getting photos early so the photographer can get to the girls.",
    icon: "/icons/tie.png",
    venue: "Bart's Apartment",
    addressLine1: "1319 N Jackson Street",
    addressLine2: "Milwaukee, WI 53202",
    requiredPasscodes: [
      "Alex Moran",
      "Bart Gebka",
      "James Conner",
      "Kolya Isterabadi",
      "Michael Conner",
    ],
    isSpecial: true,
  },
  {
    time: "1:15 PM - 2:15 PM",
    title: "Bridal Party Photos",
    icon: "/icons/camera.png",
    description:
      "We'll head to the venue together afterwards.",
    venue: "Plankinton Arcade",
    addressLine1: "161 West Wisconsin Avenue",
    addressLine2: "Milwaukee, WI 53203",
    requiredPasscodes: [
      "Alana Luebke",
      "Alex Moran",
      "Amber Micoliczyk",
      "Ashlynn Micoliczyk",
      "Bart Gebka",
      "James Conner",
      "Kolya Isterabadi",
      "Michael Conner",
      "Riley Wright",
      "Yeji Shin",
    ],
    isSpecial: true,
  },
];

const postWeddingItineraryItems: ItineraryItem[] = [
  {
    time: "10:30 AM the day after the wedding",
    title: "Brunch",
    venue: "Tupelo Honey",
    icon: "/icons/breakfast.png",
    addressLine1: "511 N Broadway #100",
    addressLine2: "Milwaukee, WI 53202",
    requiredPasscodes: [
      "Amber Micoliczyk",
      "Ashlynn Micoliczyk",
      "Danielle Conner",
      "James Conner",
      "Jim Conner",
      "Karen Micoliczyk",
      "Kolton Micoliczyk",
      "Mark Micoliczyk",
      "Michael Conner",
      "Sarah Conner"
    ]
  },
];

const WeddingDetails: React.FC = () => {
  const [passcodeInput, setPasscodeInput] = useState("");
  const [activePasscode, setActivePasscode] = useState<string | null>(null);
  const [showPasscodeInput, setShowPasscodeInput] = useState(false);

  const handlePasscodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const inputPasscode = passcodeInput.trim();

    // Find matching passcode (case-insensitive)
    const matchingPasscode = Object.keys(passcodeInfo).find(
      key => key.toLowerCase() === inputPasscode.toLowerCase()
    );

    if (matchingPasscode) {
      setActivePasscode(matchingPasscode);
      setShowPasscodeInput(false);
    } else {
      alert("Invalid passcode. Please try again.");
    }
  };

  const handleClearPasscode = () => {
    setActivePasscode(null);
    setPasscodeInput("");
  };

  // Filter items based on active passcode
  const visibleSpecialItems = allItineraryItems.filter((item) => {
    if (!item.requiredPasscodes) return false; // Not a special item
    if (!activePasscode) return false; // No passcode active
    // Check if this passcode unlocks this item (case-insensitive)
    return item.requiredPasscodes.some(
      passcode => passcode.toLowerCase() === activePasscode.toLowerCase()
    );
  });

  const visiblePostWeddingItems = postWeddingItineraryItems.filter((item) => {
    if (!item.requiredPasscodes) return false; // Not a special item
    if (!activePasscode) return false; // No passcode active
    // Check if this passcode unlocks this item (case-insensitive)
    return item.requiredPasscodes.some(
      passcode => passcode.toLowerCase() === activePasscode.toLowerCase()
    );
  });

  return (
    <section id="details" className="wedding-details">
      <div className="container">
        <h2 className="section-title">Wedding Itinerary</h2>

        {/* Passcode Section */}
        <div className="passcode-section">
          {!activePasscode ? (
            <>
              {!showPasscodeInput ? (
                <button
                  className="passcode-toggle-btn"
                  onClick={() => setShowPasscodeInput(true)}
                >
                  Have a secret code? Enter it here
                </button>
              ) : (
                <form onSubmit={handlePasscodeSubmit} className="passcode-form">
                  <div className="passcode-input-group">
                    <input
                      type="text"
                      value={passcodeInput}
                      onChange={(e) => setPasscodeInput(e.target.value)}
                      placeholder="Enter passcode"
                      className="passcode-input"
                      autoFocus
                    />
                    <button type="submit" className="passcode-submit-btn">
                      Submit
                    </button>
                    <button
                      type="button"
                      className="passcode-cancel-btn"
                      onClick={() => {
                        setShowPasscodeInput(false);
                        setPasscodeInput("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="passcode-active">
              <p className="passcode-active-text">
                Viewing: <strong>{passcodeInfo[activePasscode]?.title}</strong>
              </p>
              <button
                className="passcode-clear-btn"
                onClick={handleClearPasscode}
              >
                View Standard Schedule
              </button>
            </div>
          )}
        </div>

        <div className="details-grid">
          {/* Special Schedule Items */}
          {visibleSpecialItems.map((item, index) => (
            <div key={index} className="detail-card main-card special-card">
              <div className="main-card-header">
                {item.icon && (
                  <div className="detail-icon">
                    <img
                      src={item.icon}
                      alt={item.title}
                      width="48"
                      height="48"
                    />
                  </div>
                )}
                <h3>{item.time}</h3>
                <h4>{item.title}</h4>
              </div>
              <div className="subcards">
                <div className="subcard">
                  <div className="detail-info">
                    {item.venue && (
                      <p className="detail-venue">
                        {item.venueLink ? (
                          <u>
                            <a
                              href={item.venueLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.venue}
                            </a>
                          </u>
                        ) : (
                          item.venue
                        )}
                      </p>
                    )}
                    {(item.addressLine1 || item.addressLine2) && (
                      <p className="detail-address">
                        {item.addressLine1 && (
                          <>
                            {item.addressLine1}
                            {item.addressLine2 && <br />}
                          </>
                        )}
                        {item.addressLine2}
                      </p>
                    )}
                    {item.note && <p className="detail-note">{item.note}</p>}
                    {item.description && (
                      <p className="detail-note">{item.description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Ceremony */}
          <div className="detail-card main-card">
            <div className="main-card-header">
              <div className="detail-icon">
                <img
                  src="/icons/hall.png"
                  alt="Ceremony"
                  width="48"
                  height="48"
                />
              </div>
              <h3>3:00 - 3:30 PM</h3>
              <h4>Ceremony</h4>
            </div>
            <div className="subcards">
              <div className="subcard">
                <div className="detail-info">
                  <p className="detail-venue">
                    <u>
                      <a
                        href="https://www.bartolottas.com/bacchus"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Bacchus
                      </a>
                    </u>
                  </p>
                  <p className="detail-address">
                    925 E. Wells St.
                    <br />
                    Milwaukee, WI 53202
                  </p>
                  <p className="detail-note">
                    Street parking & valet are free. Valet starts at 2:30 PM.
                    Earliest arrival is 2:00 PM.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Cocktail Hour & Pictures */}
          <div className="detail-card main-card">
            <div className="main-card-header">
              <div className="detail-icon">
                <img
                  src="/icons/drink.png"
                  alt="Cocktail Hour"
                  width="48"
                  height="48"
                />
              </div>
              <h3>3:30 - 5:00 PM</h3>
              <h4>Cocktail Hour & Pictures</h4>
            </div>
            <div className="subcards">
              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faCocktail} size="1x" />
                </div>
                <h5>Signature Cocktails</h5>
                <div className="detail-info">
                  <p className="detail-venue">His: Blackberry Bramble</p>
                  <p className="detail-note">
                    Blackberries, Gin, Lime, Club Soda
                  </p>
                  <p className="detail-venue">Hers: Heatwave</p>
                  <p className="detail-note">
                    Pineapple, Rum, Peach Schnapps, Grenadine, Cherry
                  </p>
                </div>
              </div>

              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faFireFlameCurved} size="1x" />
                </div>
                <h5>Warm Hors d'oeuvres</h5>
                <div className="detail-info">
                  <p className="detail-venue">Grilled Wagyu Steak Tostada</p>
                  <p className="detail-note">
                    Mini corn tortilla, pico de gallo, cilantro (gf)
                  </p>
                  <p className="detail-venue">Korean BBQ Pork Meatballs</p>
                  <p className="detail-note">Gochujang sauce, scallions</p>
                </div>
              </div>

              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faSnowflake} size="1x" />
                </div>
                <h5>Cold Hors d'oeuvres</h5>
                <div className="detail-info">
                  <p className="detail-venue">Yellowfin Tuna Tartare</p>
                  <p className="detail-note">
                    Sesame-soy vinaigrette, wasabi tobiko caviar, tapioca
                    cracker, sesame aioli (gf)
                  </p>
                  <p className="detail-venue">Aged Balsamic Fig Gougere</p>
                  <p className="detail-note">LaClare chevre, microgreens (v)</p>
                  <p className="detail-venue">English Cucumber Tartelette</p>
                  <p className="detail-note">
                    Minus 8 Vinegar-marinated cucumber, dill crème fraîche,
                    preserved lemon (v)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Dinner */}
          <div className="detail-card main-card">
            <div className="main-card-header">
              <div className="detail-icon">
                <img
                  src="/icons/room-service.png"
                  alt="Dinner"
                  width="48"
                  height="48"
                />
              </div>
              <h3>5:00 - 7:00 PM</h3>
              <h4>Dinner</h4>
            </div>
            <div className="subcards">
              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faLeaf} size="1x" />
                </div>
                <h5>Salad</h5>
                <div className="detail-info">
                  <p className="detail-venue">Apple Salad</p>
                  <p className="detail-note">
                    Hundred Acre Farm's greens, pea tendrils, fresh and green
                    strawberries, candied almonds, LaClare Chevre, Meyer lemon
                    vinaigrette (gf)(v)
                  </p>
                </div>
              </div>

              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faUtensils} size="1x" />
                </div>
                <h5>Main Course</h5>
                <div className="detail-info">
                  <p className="detail-venue">See RSVP Options</p>
                </div>
              </div>

              <div className="subcard">
                <div className="subcard-icon">
                  <FontAwesomeIcon icon={faCookieBite} size="1x" />
                </div>
                <h5>Dessert</h5>
                <div className="detail-info">
                  <p className="detail-venue">Cookie Trio</p>
                  <p className="detail-venue">Assorted Macaroons</p>
                  <p className="detail-venue">Seasonal Tarts</p>
                  <p className="detail-venue">Chocolate Tarts</p>
                  <p className="detail-venue">Cream Puffs</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dancing */}
          <div className="detail-card main-card">
            <div className="main-card-header">
              <div className="detail-icon">
                <img
                  src="/icons/music-notes.png"
                  alt="Dancing"
                  width="48"
                  height="48"
                />
              </div>
              <h3>7:00 - 9:00 PM</h3>
              <h4>Dancing & Celebration</h4>
            </div>
            <div className="subcards">
              <div className="subcard">
                <div className="detail-info">
                  <p className="detail-venue">Time to celebrate!</p>
                  <p className="detail-note">
                    Join us on the dance floor for music, dancing, and more
                    celebration
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Post-Wedding Schedule Items */}
          {visiblePostWeddingItems.map((item, index) => (
            <div key={`post-${index}`} className="detail-card main-card special-card">
              <div className="main-card-header">
                {item.icon && (
                  <div className="detail-icon">
                    <img
                      src={item.icon}
                      alt={item.title}
                      width="48"
                      height="48"
                    />
                  </div>
                )}
                <h3>{item.time}</h3>
                <h4>{item.title}</h4>
              </div>
              <div className="subcards">
                <div className="subcard">
                  <div className="detail-info">
                    {item.venue && (
                      <p className="detail-venue">
                        {item.venueLink ? (
                          <u>
                            <a
                              href={item.venueLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {item.venue}
                            </a>
                          </u>
                        ) : (
                          item.venue
                        )}
                      </p>
                    )}
                    {(item.addressLine1 || item.addressLine2) && (
                      <p className="detail-address">
                        {item.addressLine1 && (
                          <>
                            {item.addressLine1}
                            {item.addressLine2 && <br />}
                          </>
                        )}
                        {item.addressLine2}
                      </p>
                    )}
                    {item.note && <p className="detail-note">{item.note}</p>}
                    {item.description && (
                      <p className="detail-note">{item.description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WeddingDetails;
